import { computed, inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { DealerProvider } from '../../../auth/dealer/dealer.provider';
import { RequestTracker } from '../../utils/requests/request-tracker';
import { EsoCrmService } from '../clients/eso-crm/eso-crm.service';
import { GcdmCustomerService } from '../clients/gcdm/gcdm-customer.service';
import { GcdmCustomerQueryOptions } from '../clients/gcdm/model/request.model';
import { RetailCrmAccountQueryOptions } from '../clients/retail-crm/model/requests/accounts.model';
import { RetailCrmService } from '../clients/retail-crm/retail-crm.service';
import { CustomerSearchQuery, VehicleSearchQuery } from '../model/customer-search-query.model';
import { CustomerVehicleRelationType } from '../model/customer-vehicle.model';
import { FoundCustomer } from '../model/found-customer.model';
import { ownsMasterDataInGcdm } from './customer-source';
import { CustomerService } from './customer.service';

export enum SearchCustomerRequestType {
  /** The NSC Database. Used for the customer search. */
  GCDM = 'GCDM',
  /** Queried by the vehicle search in GB and IE only; every other market uses Retail CRM alone (CUSTAPP-607). */
  ESO_CRM = 'ESO_CRM',
  RETAIL_CRM = 'RETAIL_CRM',
}

/**
 * This service is responsible for performing searches against Retail CRM and the NSC Database (GCDM).
 * The vehicle search runs against Retail CRM, and additionally against ESO CRM in GB and IE
 * (CUSTAPP-607).
 * It is stateless: it does not store any search results or statuses.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private readonly esoCrmService = inject(EsoCrmService);
  private readonly gcdmCustomerService = inject(GcdmCustomerService);
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly customerService = inject(CustomerService);
  private readonly dealerProvider = inject(DealerProvider);
  private readonly logger = inject(NGXLogger);

  /** The markets whose vehicle search still queries ESO CRM (CUSTAPP-607).
   * Everywhere else Retail CRM does not synchronise customer-to-vehicle relations to ESO CRM, so
   * ESO's C2Vs can be outdated and there is no way to correct them - a vehicle search against ESO
   * would return misleading results. GB and IE are the exception: their centrally hosted C2Vs are
   * kept up to date and Retail CRM consumes them too, so ESO CRM still adds relations there.
   */
  private static readonly EsoVehicleSearchMarkets = new Set(['GB', 'IE']);

  /**
   * Whether the vehicle search queries ESO CRM in the active market (CUSTAPP-607).
   *
   * Public because the search form tells the user which systems are searched. An unknown country -
   * the user is not loaded yet - counts as "no": the stricter behaviour is the safer default, as a
   * missing result is better than a misleading one.
   */
  readonly vehicleSearchIncludesEsoCrm = computed(() => {
    const country = this.dealerProvider.activeCountry();
    return !!country && SearchService.EsoVehicleSearchMarkets.has(country.toUpperCase());
  });

  searchCustomersWithTracker(query: CustomerSearchQuery) {
    const tracker = new RequestTracker<SearchCustomerRequestType>();
    return { tracker: tracker.asView(), resultPromise: this.searchCustomers(query, tracker) };
  }

  searchVinOrLicenceWithTracker(query: VehicleSearchQuery) {
    const tracker = new RequestTracker<SearchCustomerRequestType>();
    return { tracker: tracker.asView(), resultPromise: this.searchCustomersViaVinOrLicence(query, tracker) };
  }

  async searchCustomersViaVinOrLicence(query: VehicleSearchQuery, tracker?: RequestTracker<SearchCustomerRequestType>) {
    const [esoCrmCustomers, retailCrmCustomers] = await Promise.all([
      this.searchEsoCrmViaVehicle(query, tracker),
      RequestTracker.track(tracker, SearchCustomerRequestType.RETAIL_CRM, this.getRetailCrmCustomersViaVehicle(query)),
    ]);

    return this.processFoundCustomers(esoCrmCustomers, retailCrmCustomers);
  }

  async searchCustomers(
    query: CustomerSearchQuery,
    tracker?: RequestTracker<SearchCustomerRequestType>,
  ): Promise<FoundCustomer[] | undefined> {
    // Transform all empty strings to 'undefined' for consistency reasons
    const transformedQuery = this.transformEmptyToUndefinedForSearchQuery(query);

    // Search the NSC Database and Retail CRM in parallel, and merge the results afterwards.
    const [gcdmCustomers, retailCrmCustomers] = await Promise.all([
      RequestTracker.track(tracker, SearchCustomerRequestType.GCDM, this.getGcdmCustomers(transformedQuery)),
      RequestTracker.track(tracker, SearchCustomerRequestType.RETAIL_CRM, this.getRetailCrmCustomers(transformedQuery)),
    ]);

    return this.processFoundCustomers(gcdmCustomers, retailCrmCustomers, true);
  }

  private processFoundCustomers(
    nationalCustomers: FoundCustomer[] | undefined,
    retailCrmCustomers: FoundCustomer[] | undefined,
    /** CUSTAPP-613, customer search only: show the NSC Database record for customers whose master
     * data it owns. Off by default, so the vehicle search keeps Retail CRM's precedence - its
     * national source is ESO CRM, which is not the master of anything.
     */
    preferNationalForSynced = false,
  ): FoundCustomer[] | undefined {
    const mergedCustomer = this.mergeCustomers(nationalCustomers, retailCrmCustomers, preferNationalForSynced);
    const individualCustomer = this.onlyKeepIndividual(mergedCustomer);
    return this.sortCustomerByBmwOrMiniId(individualCustomer);
  }

  private onlyKeepIndividual(mergedCustomer: FoundCustomer[] | undefined) {
    return mergedCustomer?.filter((customer) => customer.isIndividual);
  }

  private transformEmptyToUndefinedForSearchQuery(query: CustomerSearchQuery): CustomerSearchQuery {
    return Object.fromEntries(Object.entries(query).map(([key, value]) => [key, value === '' ? undefined : value]));
  }

  /**
   * Merges the customers of the national data source (the NSC Database, or ESO CRM for the vehicle
   * search) with those of Retail CRM, by GCID.
   *
   * Retail CRM takes precedence, with one exception (CUSTAPP-613): when `preferNationalForSynced`
   * is set and the Retail CRM account is synced *and* carries a GCID, the NSC Database owns that
   * customer's master data - it is what the profile page reads and what an edit is written to - so
   * the list shows the NSC record instead of Retail CRM's mirror, which may lag by a sync cycle.
   *
   * Three cases, all decided by {@link ownsMasterDataInGcdm}:
   *
   * - local account (no GCID)                 -> Retail CRM
   * - account with a GCID but not synced      -> Retail CRM (the GCID is only a link, not ownership)
   * - synced account with a GCID              -> NSC Database
   *
   * The flag is off for the vehicle search: its national source is ESO CRM, which is not the master
   * of anything, so letting it win would prefer the stalest of the three copies.
   */
  private mergeCustomers(
    nationalCustomers?: FoundCustomer[],
    retailCrmCustomers?: FoundCustomer[],
    preferNationalForSynced = false,
  ): FoundCustomer[] | undefined {
    if (!nationalCustomers && !retailCrmCustomers) {
      return undefined;
    }

    let customerMap = new Map<string, FoundCustomer>();

    nationalCustomers?.forEach((customer) => {
      const gcid = customer.gcid;

      if (!gcid) {
        this.logger.warn(
          '#CDFTF0097 Customer fetched from the national data source does not contain GCID. The GCID is required for merging customers fetched from Retail CRM and the national data source.',
        );
      } else {
        customerMap = this.setOrMergeCustomer(gcid, customer, customerMap);
      }
    });

    // Snapshot taken before the Retail CRM loop starts mutating the map: the precedence decision
    // must compare against the national record, never against a Retail record written by an
    // earlier iteration (two Retail accounts can share a GCID, see #CDFTF0088).
    const nationalCustomerMap = new Map(customerMap);

    // If retail CRM customer has GCID, it is used to overwrite the national customers in customerMap.
    // Else the accountNumber is used to add the remaining customers to the map and later on to the final array.
    retailCrmCustomers?.forEach((customer) => {
      const identifyingField = customer.gcid || customer.accountNumber;

      if (!identifyingField) {
        this.logger.warn(
          '#CDFTF0098 Customer fetched from Retail CRM neither contains GCID not accountNumber. Those identifying fields are required for merging customers fetched from Retail CRM and the national data source.',
        );
        return;
      }

      customerMap = this.setOrMergeCustomer(
        identifyingField,
        this.pickMasterRecord(nationalCustomerMap.get(identifyingField), customer, preferNationalForSynced),
        customerMap,
      );
    });

    const nameTable: [string, FoundCustomer][] = Array.from(customerMap.values(), (customer) => [
      this.customerService.fullName(customer),
      customer,
    ]);
    return nameTable.toSorted((a, b) => a[0].localeCompare(b[0])).map(([_, customer]) => customer);
  }

  /**
   * Uses the identifyingId as a key and the provided customer as the value to add them to the provided map and return them.
   * If the provided key is already present within the map, the customerToVehicleRelations are merged before overwriting the value with the provided new customer.
   *
   * This is a pure function.
   * @param identifyingId The identifing field of a customer. Can either be the GCID or accountNumber, if no GCID is present
   * @param customer Customer which is to be added to the customerMap, using the identifying Field as the key
   * @param customerMap Customer is added to this map using the identifyingField as key and the customer as value. The map itself is not changed, as this is a pure function.
   * @returns The modified customerMap, containing the provided customer under the provided identifyingField as the key.
   */
  private setOrMergeCustomer(identifyingId: string, customer: FoundCustomer, customerMap: Map<string, FoundCustomer>) {
    // Copy inputs to create pure function
    const copiedCustomerMap = new Map([...customerMap]);

    // Merge customer-vehicle-relations
    const existingCustomerVehicleRelation = copiedCustomerMap.get(identifyingId)?.customerToVehicleRelations;
    const newCustomerVehicleRelation = customer?.customerToVehicleRelations;
    let mergedCustomerVehicleRelation: ReadonlySet<CustomerVehicleRelationType> | undefined;

    if (existingCustomerVehicleRelation && newCustomerVehicleRelation) {
      mergedCustomerVehicleRelation = new Set([...existingCustomerVehicleRelation, ...newCustomerVehicleRelation]);
    } else {
      // At most one relation set is present, so use it if present
      mergedCustomerVehicleRelation = existingCustomerVehicleRelation || newCustomerVehicleRelation;
    }

    copiedCustomerMap.set(identifyingId, { ...customer, customerToVehicleRelations: mergedCustomerVehicleRelation });
    return copiedCustomerMap;
  }

  private getGcdmCustomers(query: CustomerSearchQuery): Promise<FoundCustomer[] | undefined> {
    const searchParams: GcdmCustomerQueryOptions = {
      firstName: query.firstName,
      lastName: query.lastName,
      suffix: query.suffix,
      email: query.email,

      // At most one phone number is set at a time, and we need to remove spaces for GCDM
      phone: (query.phone ?? query.mobilePhone)?.replaceAll(' ', ''),

      postalCode: query.postalCode,
      city: query.city,
      street: query.street,
      houseNumber: query.houseNumber,
      taxNumber: query.taxNumber,
      nationalId: query.nationalId,
    };

    return this.gcdmCustomerService.searchCustomer(searchParams);
  }

  private getRetailCrmCustomers(query: CustomerSearchQuery): Promise<FoundCustomer[] | undefined> {
    const searchParams: RetailCrmAccountQueryOptions = {
      firstName: query.firstName,
      lastName: query.lastName,
      suffix: query.suffix,
      email: query.email,
      mobilePhone: query.mobilePhone,
      phone: query.phone,
      postalCode: query.postalCode,
      city: query.city,
      street: query.street,
      houseNumber: query.houseNumber,
      taxNumber: query.taxNumber,
      nationalId1: query.nationalId,
    };

    return this.retailCrmService.searchAccounts(searchParams);
  }

  private getRetailCrmCustomersViaVehicle(query: VehicleSearchQuery) {
    return this.retailCrmService.searchCustomersViaVehicle(query);
  }

  private getEsoCrmCustomersViaVehicle(query: VehicleSearchQuery) {
    return this.esoCrmService.searchCustomersViaVehicle(query);
  }

  private sortCustomerByBmwOrMiniId(mergedCustomer: FoundCustomer[] | undefined) {
    return mergedCustomer?.sort((a, b) => {
      if (a.bmwMiniId && !b.bmwMiniId) return -1;
      if (b.bmwMiniId && !a.bmwMiniId) return 1;
      else return 0;
    });
  }

  /**
   * Decides which record's master data is shown for a customer found in both systems.
   *
   * Returns the Retail CRM record unless the NSC Database owns the customer, in which case the
   * NSC record is returned with the Retail-CRM-owned fields kept - see {@link keepRetailOwnedFields}.
   *
   * `nationalCustomer` is undefined when the national search did not return this customer at all;
   * there is then nothing to prefer and Retail CRM's record stands.
   */
  private pickMasterRecord(
    nationalCustomer: FoundCustomer | undefined,
    retailCrmCustomer: FoundCustomer,
    preferNationalForSynced: boolean,
  ): FoundCustomer {
    if (!preferNationalForSynced || !nationalCustomer || !ownsMasterDataInGcdm(retailCrmCustomer)) {
      return retailCrmCustomer;
    }

    return this.keepRetailOwnedFields(nationalCustomer, retailCrmCustomer);
  }

  /**
   * Overlays the fields Retail CRM owns onto an NSC Database record.
   *
   * The account number is the critical one: the profile page is routed by it
   * ({@link SearchResultsContainer.tryOpenCustomerProfile}), and the NSC mapper deliberately leaves
   * it undefined. Dropping it would send a customer that exists in Retail CRM through the
   * import-to-Retail consent dialog.
   *
   * The sync flag is Retail CRM's alone, and the national id is not returned by the NSC search,
   * so both are carried over rather than lost.
   */
  private keepRetailOwnedFields(nationalCustomer: FoundCustomer, retailCrmCustomer: FoundCustomer): FoundCustomer {
    return {
      ...nationalCustomer,
      accountNumber: retailCrmCustomer.accountNumber,
      inSyncWithDirectSales: retailCrmCustomer.inSyncWithDirectSales,
      nationalId1: retailCrmCustomer.nationalId1,
      nationalId1DocumentType: retailCrmCustomer.nationalId1DocumentType,
      customerToVehicleRelations: retailCrmCustomer.customerToVehicleRelations,
    };
  }

  /**
   * Runs the ESO CRM leg of the vehicle search, in the markets that still have one (CUSTAPP-607).
   *
   * Outside GB and IE no request is sent at all, so it is not tracked either: an untracked source
   * cannot be reported as failed or in progress, and must not count towards `attemptedRequests`.
   */
  private async searchEsoCrmViaVehicle(
    query: VehicleSearchQuery,
    tracker?: RequestTracker<SearchCustomerRequestType>,
  ): Promise<FoundCustomer[] | undefined> {
    if (!this.vehicleSearchIncludesEsoCrm()) {
      return undefined;
    }
    return await RequestTracker.track(
      tracker,
      SearchCustomerRequestType.ESO_CRM,
      this.getEsoCrmCustomersViaVehicle(query),
    );
  }
}
